import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBolt, faList, faComments } from '@/fontawesome';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import { APIRoutes } from '@/services/api.routes';
import { useDayTradingWebSocket } from '@/hooks/useDayTradingWebSocket';
import { useSoundMuted } from '@/hooks/useSoundMuted';
import { useNotificationSound, showSoundBlockedToast } from '@/hooks/useNotificationSound';
import { detectSessionStateChange } from '@/utils/daytrading-change-detector';
import { handleAPIError } from '@/utils/toasts';
import { sortDayTrades } from '@/utils/daytrade-calculations';
import { getStreamSourceKey } from '@/utils/stream-info';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConnectionStatusIndicator from '@/components/daytrading/components/ConnectionStatusIndicator';
import SoundMuteButton from '@/components/daytrading/components/SoundMuteButton';
import CardSizeSlider from '@/components/daytrading/components/CardSizeSlider';
import ParticipantTradeCard from '@/components/daytrading/components/ParticipantTradeCard.tsx';
import HistoryPanel from '@/components/daytrading/components/HistoryPanel';
import ChatPanel from '@/components/daytrading/components/ChatPanel';
import StreamPiP from '@/components/daytrading/components/StreamPiP';
import StreamHeaderButton from '@/components/daytrading/components/StreamHeaderButton';
import NumberInput from '@/components/forms/NumberInput';
import { useUser } from '@/contexts/UserContext';
import type { DayTradingSessionID, DayTradeID, DayTradeAdjustementID } from '@/models/ids';
import type { DayTradingParticipantState } from '@/models/day-trading-participant';
import type { DayTradingSessionState, DayTradingSessionChat } from '@/models/day-trading-session';
import type { UserID } from '@/models/ids';
import { DAY_TRADING_ROUTES as DT_ROUTES } from '@/constants/routes';
import type { DayTradeStockCountMode } from '@/models/enums';
import type { USDCent } from '@/models/base-types';

export default function DayTradingParticipantPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const dtsid = sessionId as DayTradingSessionID;

  const {
    initialLoading,
    initialError,
    sessionState,
    participantState,
    setParticipantState,
    connectionStatus,
    error,
    sessionClosed,
    sessionDeleted,
    streamInfo,
    chat,
    setChat,
  } = useDayTradingWebSocket({
    sessionId: dtsid,
    role: 'participant',
  });

  const { user, isEmployee, isAdmin } = useUser();
  const { isMuted, toggleMuted } = useSoundMuted();

  const currentUserId = (user?.id ?? '') as UserID;
  const chatLastViewedId = chat?.lastViewed[currentUserId]?.messageID ?? null;
  const chatLatest = chat !== null && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
  const chatHasUnread =
    chatLatest !== null && chatLatest.id !== chatLastViewedId && chatLatest.userID !== currentUserId;

  const handleChatUpdate = useCallback(
    (next: DayTradingSessionChat) => setChat(next),
    [setChat],
  );

  const handleOpenStandaloneChat = useCallback(() => {
    window.open(
      `${DT_ROUTES.CHAT(dtsid)}?role=participant`,
      '_blank',
      'width=500,height=800,resizable=yes,scrollbars=yes',
    );
    setChatMinimized(true);
  }, [dtsid]);
  const { play } = useNotificationSound(isMuted);

  const prevSessionStateRef = useRef<DayTradingSessionState | null>(null);
  const isFirstStateRef = useRef(true);
  const connectedSoundPlayedRef = useRef(false);

  // Play "connected" sound on first WS connect
  useEffect(() => {
    if (connectionStatus === 'connected' && !connectedSoundPlayedRef.current) {
      connectedSoundPlayedRef.current = true;
      play('connected').then((ok) => {
        if (!ok) showSoundBlockedToast();
      });
    }
  }, [connectionStatus, play]);

  // Detect session state changes and play notification sounds
  useEffect(() => {
    if (sessionState === null) return;

    if (isFirstStateRef.current) {
      isFirstStateRef.current = false;
      prevSessionStateRef.current = sessionState;
      return;
    }

    if (sessionClosed) {
      prevSessionStateRef.current = sessionState;
      return;
    }

    if (prevSessionStateRef.current !== null) {
      const changeType = detectSessionStateChange(prevSessionStateRef.current, sessionState);
      if (changeType !== null) {
        play(changeType);
      }
    }

    prevSessionStateRef.current = sessionState;
  }, [sessionState, sessionClosed, play]);

  // Budget state (in dollars for display)
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [cardScale, setCardScale] = useState(1.0);
  const [historyMinimized, setHistoryMinimized] = useState(false);
  const [livestreamMinimized, setLivestreamMinimized] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  // Auto-restore PiP when an active stream source appears or changes
  const activeStreamSource = getStreamSourceKey(streamInfo);
  const prevStreamSourceRef = useRef(activeStreamSource);
  useEffect(() => {
    if (activeStreamSource !== prevStreamSourceRef.current) {
      prevStreamSourceRef.current = activeStreamSource;
      if (activeStreamSource !== '') {
        setLivestreamMinimized(false);
      }
    }
  }, [activeStreamSource]);

  // Sync budget from participant state whenever it changes via websocket
  const lastServerBudgetRef = useRef<number | null>(null);

  useEffect(() => {
    if (participantState !== null && participantState.budget !== lastServerBudgetRef.current && budgetDebounceRef.current === null) {
      lastServerBudgetRef.current = participantState.budget;
      setBudgetInput(Math.round(participantState.budget / 100).toString());
    }
  }, [participantState]);

  const budgetCents: USDCent = Math.round(parseFloat(budgetInput || '0') * 100);

  const updateState = useCallback(async (state: DayTradingParticipantState) => {
    try {
      const result = await APIRoutes.updateDayTradingParticipantState(dtsid, 'self', { state });
      setParticipantState(result.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren des Status');
    }
  }, [dtsid, setParticipantState]);

  // Auto-apply stockCountMode from last trade to new trades
  useEffect(() => {
    if (participantState === null || sessionState === null) return;
    const trades = sessionState.trades ?? [];
    if (trades.length < 2) return;

    const modes = participantState.stockCountMode ?? {};
    const newTrades = trades.filter((t) => modes[t.id] === undefined);
    if (newTrades.length === 0) return;

    const existingTrades = trades.filter((t) => modes[t.id] !== undefined);
    if (existingTrades.length === 0) return;

    const lastMode = modes[existingTrades[existingTrades.length - 1].id];
    if (lastMode === 'NORMAL') return;

    const updatedModes = { ...modes };
    for (const t of newTrades) {
      updatedModes[t.id] = lastMode;
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      stockCountMode: updatedModes,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [sessionState, participantState, updateState]);

  // Debounce timer for budget changes
  const budgetDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBudgetChange = useCallback((value: string) => {
    setBudgetInput(value);

    if (participantState !== null) {
      const newBudgetCents = Math.round(parseFloat(value || '0') * 100);
      lastServerBudgetRef.current = newBudgetCents;

      if (budgetDebounceRef.current !== null) {
        clearTimeout(budgetDebounceRef.current);
      }

      budgetDebounceRef.current = setTimeout(() => {
        budgetDebounceRef.current = null;
        const newState: DayTradingParticipantState = {
          ...participantState,
          budget: newBudgetCents,
          lastChange: new Date().toISOString(),
        };
        updateState(newState);
      }, 500);
    }
  }, [participantState, updateState]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (budgetDebounceRef.current !== null) {
        clearTimeout(budgetDebounceRef.current);
      }
    };
  }, []);

  const toggleAdjustement = useCallback((adjustmentId: DayTradeAdjustementID) => {
    if (participantState === null) return;

    const isProcessed = (participantState.processedAdjustements ?? []).includes(adjustmentId);
    const isSkipped = (participantState.skippedAdjustements ?? []).includes(adjustmentId);

    let newProcessed: DayTradeAdjustementID[];
    let newSkipped: DayTradeAdjustementID[];

    if (!isProcessed && !isSkipped) {
      // Neutral → Processed
      newProcessed = [...(new Set([...(participantState.processedAdjustements ?? []), adjustmentId]))];
      newSkipped = (participantState.skippedAdjustements ?? []).filter((id) => id !== adjustmentId);
    } else if (isProcessed) {
      // Processed → Skipped
      newProcessed = (participantState.processedAdjustements ?? []).filter((id) => id !== adjustmentId);
      newSkipped = [...(new Set([...(participantState.skippedAdjustements ?? []), adjustmentId]))];
    } else {
      // Skipped → Processed
      newProcessed = [...(new Set([...(participantState.processedAdjustements ?? []), adjustmentId]))];
      newSkipped = (participantState.skippedAdjustements ?? []).filter((id) => id !== adjustmentId);
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      processedAdjustements: newProcessed,
      skippedAdjustements: newSkipped,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [participantState, updateState]);

  const handleOverrideAdjustement = useCallback((adjId: DayTradeAdjustementID, priceCents: USDCent | null) => {
    if (participantState === null) return;

    let newProcessed = participantState.processedAdjustements ?? [];
    let newSkipped   = participantState.skippedAdjustements ?? [];

    const overrides = { ...(participantState.overrideAdjustementPrices ?? {}) };
    if (priceCents === null) {
      delete overrides[adjId];
    } else {
      newProcessed     = [...(new Set([...(participantState.processedAdjustements ?? []), adjId]))];
      newSkipped       = [...((participantState.skippedAdjustements ?? []).filter((id) => id !== adjId))];
      overrides[adjId] = priceCents;
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      overrideAdjustementPrices: overrides,
      processedAdjustements: newProcessed,
      skippedAdjustements: newSkipped,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [participantState, updateState]);

  const handleOverrideAdjustmentStockCount = useCallback((adjId: DayTradeAdjustementID, count: number | null) => {
    if (participantState === null) return;

    const overrides = { ...(participantState.overrideAdjustmentStockCount ?? {}) };
    if (count === null) {
      delete overrides[adjId];
    } else {
      overrides[adjId] = count;
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      overrideAdjustmentStockCount: overrides,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [participantState, updateState]);

  const handleToggleStockCountMode = useCallback((tradeId: DayTradeID) => {
    if (participantState === null || sessionState === null) return;

    const modes = { ...(participantState.stockCountMode ?? {}) };

    // Alle existierenden Trades ohne expliziten Modus auf 'NORMAL' setzen,
    // damit die Auto-Apply-Logik sie nicht als "neue Trades" behandelt
    const trades = sessionState.trades ?? [];
    for (const t of trades) {
      if (modes[t.id] === undefined) {
        modes[t.id] = 'NORMAL';
      }
    }

    const current: DayTradeStockCountMode = modes[tradeId] ?? 'NORMAL';
    modes[tradeId] = current === 'NORMAL' ? 'FIRST' : 'NORMAL';

    const newState: DayTradingParticipantState = {
      ...participantState,
      stockCountMode: modes,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [participantState, sessionState, updateState]);

  const toggleHidden = useCallback((tradeId: DayTradeID) => {
    if (participantState === null) return;

    const hidden = participantState.hiddenTrades ?? [];
    const isHidden = hidden.includes(tradeId);
    const newHidden = isHidden
      ? hidden.filter((id) => id !== tradeId)
      : [...hidden, tradeId];

    const newState: DayTradingParticipantState = {
      ...participantState,
      hiddenTrades: newHidden,
      lastChange: new Date().toISOString(),
    };
    updateState(newState);
  }, [participantState, updateState]);

  const hiddenSet = new Set(participantState?.hiddenTrades ?? []);
  const trades = sortDayTrades(sessionState?.trades ?? [], hiddenSet);

  if (initialLoading) {
    return <LoadingSpinner message="Lade Session..." />;
  }

  if (initialError !== null) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          {initialError}
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (sessionDeleted) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          Diese Session wurde gelöscht.
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      <div className="mb-6">
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faBolt} className="w-6 h-6 text-green-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Daytrading Session
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <CardSizeSlider scale={cardScale} onScaleChange={setCardScale} />
          <SoundMuteButton isMuted={isMuted} onToggle={toggleMuted} play={play} />
          <ConnectionStatusIndicator status={connectionStatus} />
        </div>
      </div>

      {/* Error */}
      {error !== null && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Session closed banner */}
      {sessionClosed && (
        <div className="mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200">
          Diese Session wurde beendet.
        </div>
      )}

      {/* Budget (centered depot section) */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-5 py-3 flex items-center gap-3">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
          Depotgröße
        </label>
        <NumberInput
          value={budgetInput}
          onChange={handleBudgetChange}
          placeholder="10000"
          suffix="$"
          ariaLabel="Depotgröße"
        />
        <div className="flex-1" />
        {chatMinimized && (
          <button
            onClick={() => setChatMinimized(false)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border cursor-pointer transition-colors ${
              chatHasUnread
                ? 'border-blue-500 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 animate-pulse'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={chatHasUnread ? 'Neue Nachrichten' : 'Chat öffnen'}
          >
            <FontAwesomeIcon icon={faComments} className="w-4 h-4" />
            Chat
            {chatHasUnread && (
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
        )}
        <StreamHeaderButton
          streamInfo={streamInfo}
          sessionClosed={sessionClosed}
          variant="viewer"
          minimized={livestreamMinimized}
          onOpenPiP={() => setLivestreamMinimized(false)}
        />
        {historyMinimized && (
          <button
            onClick={() => setHistoryMinimized(false)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
          >
            <FontAwesomeIcon icon={faList} className="w-4 h-4" />
            Historie
          </button>
        )}
      </div>

      {/* Content: trades + history */}
      <div className={historyMinimized && chatMinimized ? '' : 'grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6'}>
        {/* Trade cards grid */}
        {trades.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Noch keine Trades in dieser Session.
          </div>
        ) : (
          <div className="flex flex-wrap gap-5 self-start" style={{ zoom: cardScale }}>
            {trades.map((trade) => (
              <ParticipantTradeCard
                key={trade.id}
                trade={trade}
                budget={budgetCents}
                processedAdjustements={participantState?.processedAdjustements ?? []}
                skippedAdjustements={participantState?.skippedAdjustements ?? []}
                overrideAdjustementPrices={participantState?.overrideAdjustementPrices ?? {}}
                overrideAdjustmentStockCount={participantState?.overrideAdjustmentStockCount ?? {}}
                stockCountMode={participantState?.stockCountMode?.[trade.id] ?? 'NORMAL'}
                isHiddenTrade={participantState?.hiddenTrades?.includes(trade.id) ?? false}
                sessionClosed={sessionClosed}
                onToggleHidden={() => toggleHidden(trade.id)}
                onToggleAdjustement={(adjId) => toggleAdjustement(adjId)}
                onOverrideAdjustement={handleOverrideAdjustement}
                onOverrideAdjustmentStockCount={handleOverrideAdjustmentStockCount}
                onToggleStockCountMode={() => handleToggleStockCountMode(trade.id)}
              />
            ))}
          </div>
        )}

        {/* History + chat sidebar */}
        {(!historyMinimized || !chatMinimized) && (
          <div className="min-w-80 flex flex-col gap-6">
            {!historyMinimized && (
              <HistoryPanel history={sessionState?.history ?? []} onMinimize={() => setHistoryMinimized(true)} />
            )}
            {!chatMinimized && chat !== null && user !== null && (
              <ChatPanel
                chat={chat}
                sessionId={dtsid}
                currentUserId={currentUserId}
                canWrite={true}
                canDelete={isEmployee || isAdmin}
                isMuted={isMuted}
                onChatUpdate={handleChatUpdate}
                onMinimize={() => setChatMinimized(true)}
                onOpenStandalone={handleOpenStandaloneChat}
              />
            )}
          </div>
        )}
      </div>

      <StreamPiP
        streamInfo={streamInfo}
        minimized={livestreamMinimized}
        onMinimize={() => setLivestreamMinimized(true)}
      />
    </div>
  );
}
